'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { ToastMessage } from 'components';

interface Toast {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
}

interface ToastContextType {
  showToast: (toast: Toast) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    if (!!toast) {
      const timeoutId = setTimeout(() => {
        setToast(null);
      }, 5000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [toast]);

  const showToast = useCallback(
    (toast: Toast) => {
      setToast(toast);
    },
    [setToast]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastMessage message={toast?.message ?? ''} visible={!!toast} type={toast?.type} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
}
